import React, { useState } from "react";
import { Loader2, Calendar, MapPin, Users, Mic2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function EventOfferForm({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    submission_type: "event",
    venue_name: "",
    venue_type: "",
    country: "",
    city: "",
    event_date: "",
    event_time: "",
    show_type: "",
    performer_requirements: "",
    payment_terms: "",
    audience_size: "",
    flights_needed: false,
    accommodation_needed: false,
    organizer_contact_person: "",
    organizer_email: "",
    organizer_phone: "",
    additional_details: ""
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Event form submit triggered", formData);
    
    if (!formData.venue_name || !formData.organizer_email || !formData.event_date) {
      toast.error("Please fill in venue, email, and event date");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        name: formData.venue_name,
        email: formData.organizer_email
      };
      console.log("Sending event submission:", payload);
      
      const response = await api.post("/api/submissions/", payload);
      console.log("Event submission response:", response);
      
      toast.success("Event offer submitted!");
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      console.error("Event submission error:", error);
      const errorMsg = error?.response?.data?.detail || error?.message || "Failed to submit";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Live Show Offer</h2>
        <p className="text-gray-400">Book Gio for an event or performance</p>
      </div>

      {/* Event Info */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-400" />
          Event Information
        </h3>
        
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Venue Name <span className="text-pink-400">*</span></label>
          <input type="text" name="venue_name" value={formData.venue_name} onChange={handleChange} required
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
            placeholder="Venue or event name" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Venue Type</label>
            <select name="venue_type" value={formData.venue_type} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white focus:border-blue-500 focus:outline-none transition">
              <option value="">Select venue type</option>
              <option value="Club">Club</option>
              <option value="Sauna">Sauna</option>
              <option value="Sex Club">Sex Club</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Country</label>
            <input type="text" name="country" value={formData.country} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
              placeholder="e.g., Greece" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">City</label>
            <input type="text" name="city" value={formData.city} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
              placeholder="e.g., Athens" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Event Date <span className="text-pink-400">*</span></label>
            <input type="date" name="event_date" value={formData.event_date} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white focus:border-blue-500 focus:outline-none transition" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Event Time</label>
            <input type="time" name="event_time" value={formData.event_time} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white focus:border-blue-500 focus:outline-none transition" />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Show Type</label>
          <select name="show_type" value={formData.show_type} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white focus:border-blue-500 focus:outline-none transition">
            <option value="">Select type</option>
            <option value="Live Sex Show">Live Sex Show</option>
            <option value="Solo Stage Show">Solo Stage Show</option>
            <option value="Stage Performance">Stage Performance</option>
            <option value="Club Appearance">Club Appearance</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Payment Terms</label>
            <input type="text" name="payment_terms" value={formData.payment_terms} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
              placeholder="e.g., €1000 flat fee" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Expected Audience Size</label>
            <input type="text" name="audience_size" value={formData.audience_size} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
              placeholder="e.g., 100-200 people" />
          </div>
        </div>
      </div>

      {/* Organizer Contact */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Organizer Contact
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Contact Person</label>
            <input type="text" name="organizer_contact_person" value={formData.organizer_contact_person} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
              placeholder="Your name" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Email <span className="text-pink-400">*</span></label>
            <input type="email" name="organizer_email" value={formData.organizer_email} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
              placeholder="organizer@example.com" />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Phone</label>
          <input type="tel" name="organizer_phone" value={formData.organizer_phone} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition"
            placeholder="+1 (555) 123-4567" />
        </div>

        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
            <input type="checkbox" name="flights_needed" checked={formData.flights_needed} onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 text-blue-600 focus:ring-blue-500" />
            <span>Flights Needed</span>
          </label>
          <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
            <input type="checkbox" name="accommodation_needed" checked={formData.accommodation_needed} onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 text-blue-600 focus:ring-blue-500" />
            <span>Accommodation Needed</span>
          </label>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Performer Requirements & Additional Details</label>
          <textarea name="performer_requirements" value={formData.performer_requirements} onChange={handleChange} rows={4}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition resize-none"
            placeholder="Describe what you need..." />
        </div>
      </div>

      <button type="submit" disabled={loading}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold text-lg hover:from-blue-600 hover:to-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-500/50">
        {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Submitting...</>) : "Submit Event Offer"}
      </button>
    </form>
  );
}